import { useState } from "react";
import { motion } from "framer-motion";
import { Navigate } from "react-router-dom";
import { LayoutDashboard, Users as UsersIcon, BookText, Cpu, Activity, Sprout } from "lucide-react";
import { useProfile } from "../../layout/AppShell";
import { cn } from "../../lib/format";
import Overview from "./admin/Overview";
import Users from "./admin/Users";
import Content from "./admin/Content";
import Models from "./admin/Models";
import Health from "./admin/Health";
import Curriculum from "./admin/Curriculum";

type Tab = "overview" | "users" | "content" | "curriculum" | "models" | "health";

const TABS: Array<{ id: Tab; label: string; icon: typeof LayoutDashboard }> = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: UsersIcon },
  { id: "content", label: "Personalities", icon: BookText },
  { id: "curriculum", label: "Curriculum", icon: Sprout },
  { id: "models", label: "Models", icon: Cpu },
  { id: "health", label: "Health", icon: Activity },
];

export default function Admin() {
  const { profile } = useProfile();
  const [tab, setTab] = useState<Tab>("overview");

  if (!profile.is_admin) return <Navigate to="/app/today" replace />;

  return (
    <div>
      <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-faint">
        admin · restricted
      </div>
      <h1 className="mt-3 font-serif text-3xl md:text-4xl leading-tight">
        Behind the <span className="italic text-terracotta">curtain.</span>
      </h1>
      <p className="mt-2 max-w-xl font-serif italic text-ink-soft text-base">
        Numbers, models, and the things that should never break in front of a user.
      </p>

      {/* Tab strip — quiet, mono labels, terracotta active */}
      <div className="mt-8 flex gap-1 overflow-x-auto border-b border-[rgba(42,36,26,0.12)] -mb-px">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              "flex items-center gap-2 px-4 py-2.5 text-[13px] whitespace-nowrap border-b-2 transition-colors",
              tab === t.id
                ? "border-terracotta text-terracotta font-medium"
                : "border-transparent text-ink-soft hover:text-terracotta",
            )}
          >
            <t.icon size={13} />
            {t.label}
          </button>
        ))}
      </div>

      <motion.div
        key={tab}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="mt-8"
      >
        {tab === "overview" && <Overview />}
        {tab === "users" && <Users />}
        {tab === "content" && <Content />}
        {tab === "curriculum" && <Curriculum />}
        {tab === "models" && <Models />}
        {tab === "health" && <Health />}
      </motion.div>
    </div>
  );
}
